import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getEquipmentOptions, createEquipmentOption, updateEquipmentOption, deleteEquipmentOption } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetEquipmentOptions = () => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["equipment-options"],
    queryFn: () => getEquipmentOptions(),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateEquipmentOption = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createEquipmentOption(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["equipment-options"] });
      
      // Show success notification
      showNotification('success', 'Equipment created successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=equipments');
    },
    onError: (error) => {
      console.error("Error creating equipment option:", error);
      
      // Show error notification
      let errorMessage = 'Error creating equipment. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateEquipmentOption = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateEquipmentOption(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment-options"] });
      
      // Show success notification
      showNotification('success', 'Equipment updated successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=equipments');
    },
    onError: (error) => {
      console.error("Error updating equipment option:", error);
      
      // Show error notification
      let errorMessage = 'Error updating equipment. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteEquipmentOption = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteEquipmentOption(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment-options"] });
      
      // Show success notification
      showNotification('success', 'Equipment deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting equipment option:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting equipment. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
}; 